import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ClickableAvatar } from "../components/ClickableAvatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { AddProgressModal } from "../components/AddProgressModal";
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameDay, addMonths, subMonths, isToday, isSameMonth,
} from "date-fns";
import {
  Target, TrendingUp, ChevronLeft, ChevronRight,
  AlertTriangle, CheckCircle, Clock, Activity,
  CalendarDays, List, Search, Calendar as CalendarIcon, MessageSquare, Plus, Phone,
  Eye,
} from "lucide-react";
import { WhatsApp } from "../components/ui/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const toLocalDateStr = (date) => format(new Date(date), "yyyy-MM-dd");

const parseMonthParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 2) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
      return new Date(year, month - 1, 1);
    }
  }
  return null;
};

const parseDateParam = (param) => {
  if (!param) return null;
  const parts = param.split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!isNaN(year) && !isNaN(month) && !isNaN(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return new Date(year, month - 1, day);
    }
  }
  return null;
};

const PhotoAvatar = ({ photo, name, size = "h-9 w-9" }) => (
  <ClickableAvatar
    src={photo}
    name={name}
    className={`${size} border border-border shrink-0`}
    fallbackClassName="bg-primary/10 text-primary text-xs font-heading"
  />
);

const GOAL_STATUS_COLOR = {
  Active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
  Completed: "bg-blue-500/15 text-blue-500 border-blue-500/20",
  Paused: "bg-amber-500/15 text-amber-500 border-amber-500/20",
};

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

export default function ProgressCalendarPage() {
  const { currentBranch, hasPermission } = useAuth();
  const navigate = useNavigate();

  const canViewProgress = hasPermission("view_progress");
  const canAddProgress = hasPermission("add_progress");

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "calendar";
  const setActiveTab = (tab) => {
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev);
      nextParams.set("tab", tab);
      return nextParams;
    });
  };

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [existingEntry, setExistingEntry] = useState(null);

  // ── Calendar state ─────────────────────────────────────────────────────────
  const [currentDate, setCurrentDate] = useState(() => {
    const mParam = new URLSearchParams(window.location.search).get("month");
    const parsedM = parseMonthParam(mParam);
    if (parsedM) return parsedM;
    const dParam = new URLSearchParams(window.location.search).get("date");
    const parsedD = parseDateParam(dParam);
    if (parsedD) return new Date(parsedD.getFullYear(), parsedD.getMonth(), 1);
    return new Date();
  });

  const selectedDate = useMemo(() => {
    const dParam = searchParams.get("date");
    const parsedD = parseDateParam(dParam);
    if (parsedD) return parsedD;
    return new Date(); // default to today
  }, [searchParams]);

  useEffect(() => {
    const mParam = searchParams.get("month");
    const parsedM = parseMonthParam(mParam);
    const dParam = searchParams.get("date");
    const parsedD = parseDateParam(dParam);

    let targetMonth;
    if (parsedM) {
      targetMonth = parsedM;
    } else if (parsedD) {
      targetMonth = new Date(parsedD.getFullYear(), parsedD.getMonth(), 1);
    } else {
      targetMonth = new Date();
    }

    if (
      targetMonth.getFullYear() !== currentDate.getFullYear() ||
      targetMonth.getMonth() !== currentDate.getMonth()
    ) {
      setCurrentDate(new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1));
    }
  }, [searchParams, currentDate]);

  const handleMonthChange = (newMonth) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("month", format(newMonth, "yyyy-MM"));
    nextParams.delete("date");  // clear selected day on month navigation
    setSearchParams(nextParams);
  };

  const handleDayClick = (day) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("date", format(day, "yyyy-MM-dd"));
    nextParams.set("month", format(day, "yyyy-MM"));
    setSearchParams(nextParams);
  };
  const [reminderFilter, setReminderFilter] = useState("all");
  const [calLoading, setCalLoading] = useState(true);
  const [calData, setCalData] = useState({
    todayProgressCount: 0, pendingReminderCount: 0,
    overdueReminderCount: 0, upcomingReminderCount: 0,
    pendingReminders: [], overdueReminders: [], upcomingReminders: [],
    progressByDate: {}, activeGoals: [],
  });

  // ── All Goals state ────────────────────────────────────────────────────────
  const [allGoals, setAllGoals] = useState([]);
  const [goalsTotal, setGoalsTotal] = useState(0);
  const [goalsPage, setGoalsPage] = useState(1);
  const [goalsLimit, setGoalsLimit] = useState(20);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalSearch, setGoalSearch] = useState("");
  const [goalStatus, setGoalStatus] = useState("");

  useEffect(() => { if (canViewProgress) fetchCalData(); }, [currentDate, currentBranch]);
  useEffect(() => {
    if (activeTab === "goals" && canViewProgress) fetchGoals();
  }, [activeTab, goalsPage, goalsLimit, goalStatus, currentBranch]);
  useEffect(() => {
    if (activeTab !== "goals") return;
    const t = setTimeout(() => { setGoalsPage(1); fetchGoals(); }, 400);
    return () => clearTimeout(t);
  }, [goalSearch]);

  const fetchCalData = async () => {
    setCalLoading(true);
    try {
      const startDate = format(startOfMonth(currentDate), "yyyy-MM-dd");
      const endDate = format(endOfMonth(currentDate), "yyyy-MM-dd");
      const today = toLocalDateStr(new Date());
      const params = new URLSearchParams({ startDate, endDate, today });
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const res = await api.get(`/progress/calendar?${params.toString()}`);
      setCalData(res.data);
    } catch { toast.error("Failed to load progress calendar"); }
    finally { setCalLoading(false); }
  };

  const fetchGoals = async () => {
    setGoalsLoading(true);
    try {
      const params = new URLSearchParams();
      if (goalStatus) params.append("status", goalStatus);
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const res = await api.get(`/goals?${params.toString()}`);
      let list = res.data.goals || [];
      if (goalSearch.trim()) {
        const q = goalSearch.trim().toLowerCase();
        list = list.filter(g =>
          g.member?.name?.toLowerCase().includes(q) ||
          g.name?.toLowerCase().includes(q)
        );
      }
      setAllGoals(list);
      setGoalsTotal(list.length);
      setGoalsPage(1);
    } catch { toast.error("Failed to load goals"); }
    finally { setGoalsLoading(false); }
  };

  const pagedGoals = allGoals.slice((goalsPage - 1) * goalsLimit, goalsPage * goalsLimit);
  const goalsTotalPages = Math.ceil(goalsTotal / goalsLimit) || 1;

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const todayStr = toLocalDateStr(new Date());

  const getProgressForDay = (date) => calData.progressByDate[toLocalDateStr(date)] || [];

  const getRemindersForDay = (date) => {
    if (!date) return [];
    const dateStr = toLocalDateStr(date);
    const isTodayDate = dateStr === todayStr;
    const all = [
      ...(calData.overdueReminders || []),
      ...(calData.pendingReminders || []),
      ...(calData.upcomingReminders || []),
    ];

    // Deduplicate all by id
    const uniqueAll = [];
    const seen = new Set();
    all.forEach(g => {
      if (!seen.has(g.id)) {
        seen.add(g.id);
        uniqueAll.push(g);
      }
    });

    if (isTodayDate) {
      let pool = uniqueAll.filter(g => {
        const rd = (g.nextReminderDate || "").slice(0, 10);
        return rd === todayStr || (!rd && (calData.pendingReminders || []).some(p => p.id === g.id));
      });
      if (pool.length === 0) pool = [...(calData.pendingReminders || [])];
      return pool;
    } else {
      return uniqueAll.filter(g => (g.nextReminderDate || "").slice(0, 10) === dateStr);
    }
  };

  const remindersForSelectedDate = () => {
    const dateStr = selectedDate ? toLocalDateStr(selectedDate) : todayStr;
    const isTodaySelected = dateStr === todayStr;
    const all = [
      ...(calData.overdueReminders || []),
      ...(calData.pendingReminders || []),
      ...(calData.upcomingReminders || []),
    ];

    // Deduplicate all by id
    const uniqueAll = [];
    const seen = new Set();
    all.forEach(g => {
      if (!seen.has(g.id)) {
        seen.add(g.id);
        uniqueAll.push(g);
      }
    });

    let pool;
    if (isTodaySelected) {
      pool = uniqueAll.filter(g => {
        const rd = (g.nextReminderDate || "").slice(0, 10);
        return rd === todayStr || (!rd && (calData.pendingReminders || []).some(p => p.id === g.id));
      });
      if (pool.length === 0) pool = [...(calData.pendingReminders || [])];
    } else {
      pool = uniqueAll.filter(g => (g.nextReminderDate || "").slice(0, 10) === dateStr);
    }
    if (reminderFilter === "overdue") return pool.filter(g => (calData.overdueReminders || []).some(o => o.id === g.id));
    if (reminderFilter === "pending") return pool.filter(g => (calData.pendingReminders || []).some(o => o.id === g.id));
    if (reminderFilter === "upcoming") return pool.filter(g => (calData.upcomingReminders || []).some(o => o.id === g.id));
    return pool;
  };

  const reminderList = remindersForSelectedDate();
  const progressForDay = selectedDate ? getProgressForDay(selectedDate) : [];

  const monthStats = useMemo(() => {
    let prCount = 0;
    Object.values(calData.progressByDate || {}).forEach(list => {
      prCount += list.length;
    });

    const currentYearMonth = format(currentDate, "yyyy-MM");
    const todayYearMonth = format(new Date(), "yyyy-MM");

    const isGoalInCurrentMonth = (goal) => {
      const rd = (goal.nextReminderDate || "").slice(0, 10);
      if (rd) {
        return rd.startsWith(currentYearMonth);
      }
      return currentYearMonth === todayYearMonth;
    };

    const overdueInMonth = (calData.overdueReminders || []).filter(isGoalInCurrentMonth).length;
    const pendingInMonth = (calData.pendingReminders || []).filter(isGoalInCurrentMonth).length;
    const upcomingInMonth = (calData.upcomingReminders || []).filter(isGoalInCurrentMonth).length;

    return {
      progress: prCount,
      overdue: overdueInMonth,
      pending: pendingInMonth,
      upcoming: upcomingInMonth,
    };
  }, [calData, currentDate]);

  if (!canViewProgress) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black font-heading tracking-tight uppercase">Progress</h1>
          <p className="text-sm text-muted-foreground mt-1">Track member progress and goal reminders</p>
        </div>
        <Card className="bg-card border-border p-12 text-center">
          <Target className="h-14 w-14 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-heading mb-2">Access Restricted</h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            You don&apos;t have permission to view the Progress page. Contact your gym manager to enable access.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2 md:gap-3" data-testid="progress-calendar-page">

      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">Progress</h1>
        <p className="text-sm text-muted-foreground mt-1">Track member progress and goal reminders</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 space-y-2 md:space-y-3">
        <TabsList className="bg-secondary/50 p-1 justify-start">
          <TabsTrigger value="calendar" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-calendar">
            <CalendarDays className="h-3.5 w-3.5 mr-2" />Calendar
          </TabsTrigger>
          <TabsTrigger value="goals" className="font-heading uppercase text-xs tracking-wide" data-testid="tab-goals">
            <List className="h-3.5 w-3.5 mr-2" />All Goals
          </TabsTrigger>
          {/* Search + Status filter */}
          {
            activeTab === "goals" && (
              <div className="flex flex-row gap-3 justify-end ml-auto hidden md:flex">
                <div className="relative flex-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search member or goal name..."
                    value={goalSearch}
                    onChange={e => setGoalSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={goalStatus || "all"} onValueChange={v => { setGoalStatus(v === "all" ? "" : v); setGoalsPage(1); }}>
                  <SelectTrigger className="w-44"><SelectValue placeholder="All Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Paused">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )
          }
        </TabsList>
        {/* ══ CALENDAR TAB ══ */}
        <TabsContent value="calendar" className="mt-0">
          <div className="flex flex-col gap-6">
            {/* Stats Bar */}
            <div className="relative z-0 flex overflow-x-auto gap-2 pb-1 -mx-4 px-4 md:grid md:grid-cols-4 md:gap-3 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {[
                { count: calData.todayProgressCount, icon: CheckCircle, color: "emerald", label: "Today" },
                { count: calData.pendingReminderCount, icon: Clock, color: "amber", label: "Pending" },
                { count: calData.overdueReminderCount, icon: AlertTriangle, color: "red", label: "Overdue" },
                { count: calData.upcomingReminderCount, icon: Target, color: "blue", label: "Upcoming" },
              ].map(({ count, icon: Icon, color, label }) => (
                <Card key={label} className="bg-card border-border shrink-0 min-w-[140px] md:min-w-0">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-${color}-500/15 flex items-center justify-center shrink-0`}>
                      <Icon className={`h-5 w-5 text-${color}-500`} />
                    </div>
                    <div>
                      <p className="text-2xl font-black font-heading">{count || 0}</p>
                      <p className="text-[10px] text-muted-foreground font-mono uppercase">{label}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 items-start">
              {/* Left: Calendar Card */}
              <Card className="lg:col-span-5 bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 py-2">
                  <div className="flex flex-col gap-1">
                    <CardTitle className="text-sm font-heading uppercase tracking-wide leading-none">Calendar</CardTitle>
                    <div className="flex items-center gap-2 text-[10px] font-mono font-bold leading-none mt-1">
                      {monthStats.progress > 0 && <span className="text-emerald-500">PR: {monthStats.progress}</span>}
                      {monthStats.overdue > 0 && <span className="text-red-500">OD: {monthStats.overdue}</span>}
                      {monthStats.pending > 0 && <span className="text-amber-500">PD: {monthStats.pending}</span>}
                      {monthStats.upcoming > 0 && <span className="text-blue-500">UP: {monthStats.upcoming}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleMonthChange(subMonths(currentDate, 1))} data-testid="prev-month-btn">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium min-w-[100px] text-center">{format(currentDate, "MMMM yyyy")}</span>
                    <Button variant="ghost" size="icon" onClick={() => handleMonthChange(addMonths(currentDate, 1))} data-testid="next-month-btn">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-1">
                  {calLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {DAYS_OF_WEEK.map(d => (
                          <div key={d.key} className="text-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground py-2">{d.label}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-0.5">
                        {daysInMonth.map((day, idx) => {
                          const dayOfWeek = day.getDay();
                          const isFirstDay = idx === 0;
                          const dateStr = format(day, "yyyy-MM-dd");

                          const dayProgress = getProgressForDay(day);
                          const dayReminders = getRemindersForDay(day);
                          const totalCount = dayProgress.length + dayReminders.length;

                          const isSelected = selectedDate && isSameDay(day, selectedDate);
                          const isTodayDay = isToday(day);

                          const progressCount = dayProgress.length;
                          const overdueCount = dayReminders.filter(g => (calData.overdueReminders || []).some(o => o.id === g.id)).length;
                          const pendingCount = dayReminders.filter(g => (calData.pendingReminders || []).some(p => p.id === g.id)).length;
                          const upcomingCount = dayReminders.filter(g => (calData.upcomingReminders || []).some(u => u.id === g.id)).length;

                          return (
                            <div key={day.toISOString()}
                              style={{ gridColumn: isFirstDay ? dayOfWeek + 1 : undefined }}
                              onClick={() => handleDayClick(day)}
                              className={`min-h-[60px] sm:min-h-[80px] p-1 sm:p-2 rounded-sm border cursor-pointer transition-all ${isTodayDay ? "border-primary bg-primary/5 shadow-[0_0_10px_rgba(var(--primary),0.1)]"
                                : isSelected ? "border-primary bg-primary/5"
                                  : totalCount > 0 ? "border-border bg-secondary/30 hover:bg-secondary/50"
                                    : "border-border/50 bg-background hover:bg-secondary/20"
                                }`} data-testid={`calendar-day-${dateStr}`}
                            >
                              <div className={`text-[10px] font-medium mb-1 flex justify-between ${isTodayDay ? "text-primary" : ""}`}>
                                <span>{format(day, "d")}</span>
                                {totalCount > 0 && (
                                  <span className={`text-[9px] font-medium w-4 h-4 flex items-center justify-center rounded-full ${progressCount > 0 ? "bg-emerald-500 text-white"
                                    : overdueCount > 0 ? "bg-red-500 text-white"
                                      : pendingCount > 0 ? "bg-amber-500 text-white"
                                        : "bg-blue-500 text-white"
                                    }`}>
                                    {totalCount}
                                  </span>
                                )}
                              </div>
                              {totalCount > 0 && (
                                <div className="space-y-1">
                                  <div className="flex gap-1 flex-wrap">
                                    {progressCount > 0 && <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" title={`${progressCount} Progress Logged`} />}
                                    {overdueCount > 0 && <div className="h-1.5 w-1.5 rounded-full bg-red-500" title={`${overdueCount} Overdue Reminder`} />}
                                    {pendingCount > 0 && <div className="h-1.5 w-1.5 rounded-full bg-amber-500" title={`${pendingCount} Pending Reminder`} />}
                                    {upcomingCount > 0 && <div className="h-1.5 w-1.5 rounded-full bg-blue-500" title={`${upcomingCount} Upcoming Reminder`} />}
                                  </div>
                                  <div className="flex flex-wrap gap-x-1.5 gap-y-0 text-[9px] font-mono font-bold leading-none mt-1">
                                    {progressCount > 0 && <span className="text-emerald-500">PR:{progressCount}</span>}
                                    {overdueCount > 0 && <span className="text-red-500">OD:{overdueCount}</span>}
                                    {pendingCount > 0 && <span className="text-amber-500">PD:{pendingCount}</span>}
                                    {upcomingCount > 0 && <span className="text-blue-500">UP:{upcomingCount}</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6 mt-4 pt-4 border-t border-border flex-wrap">
                        {[
                          { color: "bg-emerald-500", label: "Progress Logged" },
                          { color: "bg-red-500", label: "Overdue Reminder" },
                          { color: "bg-amber-500", label: "Pending Reminder" },
                          { color: "bg-blue-500", label: "Upcoming Reminder" },
                          { color: "text-emerald-500", label: "PR", desc: "Progress Logged", type: "text" },
                          { color: "text-red-500", label: "OD", desc: "Overdue Reminder", type: "text" },
                          { color: "text-amber-500", label: "PD", desc: "Pending Reminder", type: "text" },
                          { color: "text-blue-500", label: "UP", desc: "Upcoming Reminder", type: "text" },
                        ].map(l => (
                          <div key={l.label} className="flex items-center gap-1.5">
                            {l.type === "text" ? (
                              <span className={`text-[9px] font-mono font-bold ${l.color}`}>{l.label}</span>
                            ) : (
                              <div className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                            )}
                            <span className="text-[11px] text-muted-foreground">{l.desc || l.label}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Right: Daily Details Card */}
              <Card className="lg:col-span-7 bg-card border-border flex flex-col h-full min-h-[500px] lg:h-[calc(100vh-280px)]">
                <CardHeader className="px-3 py-2 border-b border-border shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4 text-primary" />
                      <CardTitle className="text-sm font-heading uppercase tracking-wide">
                        {selectedDate ? format(selectedDate, "EEEE, MMM d, yyyy") : "Daily Details"}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-0 scroll-smooth">
                  {!selectedDate ? (
                    <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground opacity-60">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
                        <CalendarIcon className="h-6 w-6" />
                      </div>
                      <p className="text-sm font-medium">Select a day from the calendar</p>
                      <p className="text-xs mt-1">to view scheduled events</p>
                    </div>
                  ) : (
                    <>
                      {/* Day Summary */}
                      <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-xl border border-border m-3 shrink-0">
                        <div className="flex items-baseline gap-2">
                          <p className="text-lg font-black font-heading leading-none">
                            {reminderList.length + progressForDay.length}
                          </p>
                          <p className="text-[10px] text-muted-foreground font-mono uppercase">Total Daily Items</p>
                        </div>
                        <div className="flex gap-1.5">
                          {reminderList.length > 0 && (
                            <Badge className="bg-blue-500/15 text-blue-500 border border-blue-500/20 text-[9px] h-4 px-1.5">
                              Reminders: {reminderList.length}
                            </Badge>
                          )}
                          {progressForDay.length > 0 && (
                            <Badge className="bg-emerald-500/15 text-emerald-500 border border-emerald-500/20 text-[9px] h-4 px-1.5">
                              Logged Progress: {progressForDay.length}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="p-3 space-y-4">
                        {/* Reminders section */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Goal Reminders</p>
                            <Select value={reminderFilter} onValueChange={setReminderFilter}>
                              <SelectTrigger className="w-[110px] h-7 text-xs shrink-0">
                                <SelectValue placeholder="Filter" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="overdue">Overdue</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="upcoming">Upcoming</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {calLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                            </div>
                          ) : reminderList.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic py-2">No reminders for this day.</p>
                          ) : (
                            <div className="space-y-2">
                              {reminderList.map((goal, i) => {
                                const isOverdue = (calData.overdueReminders || []).some(g => g.id === goal.id);
                                const isPending = (calData.pendingReminders || []).some(g => g.id === goal.id);
                                return (
                                  <div
                                    key={goal.id || i}
                                    className="group bg-card border border-border rounded-xl shadow-sm overflow-hidden"
                                  >
                                    <div className="px-3 py-2 flex items-center justify-between gap-3 select-none">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <PhotoAvatar photo={goal.member?.photo} name={goal.member?.name} size="h-7 w-7" />
                                        <div className="min-w-0 flex-1 flex flex-col justify-center">
                                          <div className="flex items-center gap-1.5 min-w-0">
                                            <span className="font-semibold text-sm truncate">{goal.member?.name || "Unknown"}</span>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-5 w-5 text-muted-foreground hover:text-primary shrink-0"
                                              onClick={() => navigate(`/members/${goal.memberId}?tab=progress`)}
                                              title="View Profile"
                                            >
                                              <Eye className="h-3.5 w-3.5" />
                                            </Button>
                                          </div>
                                          {goal.member?.phone && (
                                            <div className="flex items-center gap-2">
                                              <span className="text-[9px] md:text-xs text-muted-foreground font-normal">{goal.member?.phone}</span>
                                              <div className="flex gap-1  md:gap-2">
                                                <a
                                                  href={`tel:${goal.member.phone}`}
                                                  className="text-[9px] text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/30 px-1.5 py-0.5 rounded hover:bg-sky-100 dark:hover:bg-sky-950/60 flex items-center gap-1 font-semibold transition-colors"
                                                >
                                                  <Phone className="h-2.5 w-2.5" /> Call
                                                </a>
                                                <a
                                                  href={`https://wa.me/${goal.member.phone}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-[9px] text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-1.5 py-0.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-950/60 flex items-center gap-1 font-semibold transition-colors"
                                                >
                                                  <WhatsApp className="h-2.5 w-2.5" /> <span className="hidden md:block">WhatsApp</span>
                                                </a>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <Badge className={`text-[9px] font-mono shrink-0 border-0 py-0.5 px-1.5 ${isOverdue ? "bg-red-500/15 text-red-500"
                                        : isPending ? "bg-amber-500/15 text-amber-500"
                                          : "bg-blue-500/15 text-blue-500"
                                        }`}>
                                        {isOverdue ? "OVERDUE" : isPending ? "TODAY" : "UPCOMING"}
                                      </Badge>
                                    </div>
                                    <div className="px-3 pb-2 flex items-center justify-between gap-4 text-xs font-mono text-muted-foreground">
                                      <div className="flex flex-col justify-center gap-2 flex-1">
                                        <span className="text-xs text-muted-foreground font-normal truncate"> {goal.name}</span>
                                        <div className="flex items-center gap-2 max-w-[200px]">
                                          <Progress value={goal.progressPercent || 0} className="h-1 flex-1" />
                                          <span className="text-[10px] shrink-0">{goal.progressPercent || 0}%</span>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        {goal.nextReminderDate && (
                                          <span className="text-[10px]">
                                            Due: {(goal.nextReminderDate || "").slice(0, 10)}
                                          </span>
                                        )}
                                        {canAddProgress && goal.status === "Active" && (
                                          <Button
                                            size="sm"
                                            className="text-md uppercase tracking-wider font-heading px-2 py-2"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedGoal(goal);
                                              setExistingEntry(null);
                                              setShowProgressModal(true);
                                            }}
                                          >
                                            <TrendingUp className="h-3.5 w-3.5 mr-1" /> Add <span className="hidden md:block">Progress</span>
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <Separator />

                        {/* Progress Logged section */}
                        <div className="space-y-2">
                          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Progress Activity</p>

                          {calLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                            </div>
                          ) : progressForDay.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic py-2">No progress activity logged for this day.</p>
                          ) : (
                            <div className="divide-y divide-border">
                              {progressForDay.map((entry, i) => (
                                <div
                                  key={entry.id || i}
                                  className="py-2.5 flex flex-col gap-1.5 cursor-pointer hover:bg-secondary/15 transition-colors px-2 -mx-2 rounded-lg"
                                  onClick={() => navigate(`/members/${entry.memberId}?tab=progress`)}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      <PhotoAvatar photo={entry.member?.photo} name={entry.member?.name} size="h-7 w-7" />
                                      <p className="font-semibold text-sm truncate flex items-center gap-1.5 min-w-0">
                                        <span className="truncate">{entry.member?.name || "Unknown"}</span>
                                        <span className="text-xs text-muted-foreground font-normal truncate">• {entry.goal?.name || "—"}</span>
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <Badge className="text-[9px] font-mono border bg-emerald-500/15 text-emerald-500 border-emerald-500/20 shrink-0 py-0.5 px-1.5">
                                        LOGGED
                                      </Badge>
                                      {canAddProgress && entry.goal && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-6 text-[9px] uppercase tracking-wider font-heading px-2 py-0 hover:bg-secondary/80 text-foreground"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedGoal(entry.goal);
                                            setExistingEntry(entry);
                                            setShowProgressModal(true);
                                          }}
                                        >
                                          Edit
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                  {(entry.measurements?.length > 0 || entry.notes) && (
                                    <div className="pl-9 flex flex-wrap items-center gap-2">
                                      {entry.measurements?.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                          {entry.measurements.map(m => (
                                            <Badge key={m.metric} variant="outline" className="text-[9px] font-mono py-0 px-1">
                                              {m.metric}: {m.value}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                      {entry.notes && (
                                        <span className="text-[10px] text-muted-foreground italic leading-normal truncate">
                                          {entry.notes}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ══ ALL GOALS TAB ══ */}
        <TabsContent value="goals" className="flex-1 flex flex-col min-h-0 mt-0">
          <div className="flex flex-col flex-1 min-h-0 gap-4">

            {/* Search + Status filter */}
            <div className="flex flex-row gap-3 justify-end md:hidden">
              <div className="relative flex-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search member or goal name..."
                  value={goalSearch}
                  onChange={e => setGoalSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={goalStatus || "all"} onValueChange={v => { setGoalStatus(v === "all" ? "" : v); setGoalsPage(1); }}>
                <SelectTrigger className="w-44"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>


            <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0  md:mx-0 rounded-lg">
              {goalsLoading ? (
                <div className="flex items-center justify-center py-16 gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                  <span className="text-muted-foreground text-sm">Loading...</span>
                </div>
              ) : pagedGoals.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
                  No goals found
                </div>
              ) : (
                <>
                  {/* ── Mobile card list (hidden md+) ── */}
                  <div className="md:hidden overflow-x-hidden overflow-y-auto flex-1 divide-y divide-border">
                    {pagedGoals.map((goal, idx) => {
                      const reminderStr = (goal.nextReminderDate || "").slice(0, 10);
                      const reminderColor =
                        reminderStr && reminderStr < todayStr ? "text-red-500" :
                          reminderStr === todayStr ? "text-amber-500" :
                            "text-muted-foreground";
                      return (
                        <div
                          key={goal.id}
                          className={`p-3 cursor-pointer hover:bg-secondary/10 transition-colors ${idx !== pagedGoals.length - 1 ? "border-b border-border" : ""
                            }`}
                          onClick={() => navigate(`/members/${goal.memberId}?tab=progress`)}
                        >
                          <div className="flex items-start justify-between gap-3 min-w-0">
                            {/* Left side: Avatar + Info */}
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                              <PhotoAvatar photo={goal.member?.photo} name={goal.member?.name} />
                              <div className="min-w-0 flex-1 space-y-1">
                                {/* Line 1: Name, Member Number, Phone */}
                                <div className="flex items-baseline gap-1.5 flex-wrap">
                                  <p className="font-semibold text-sm leading-none truncate">
                                    {goal.member?.name || <span className="italic text-muted-foreground">Unknown</span>}
                                  </p>
                                  {goal.member?.memberNumber != null && (
                                    <span className="text-[10px] font-mono text-muted-foreground shrink-0">#{goal.member?.memberNumber}</span>
                                  )}
                                  {goal.member?.phone && (
                                    <span className="text-[10px] text-muted-foreground font-mono">• {goal.member?.phone}</span>
                                  )}
                                </div>


                              </div>
                            </div>

                            {/* Right side: Badge, Next Reminder, Actions */}
                            <div className="flex flex-col items-end gap-1.5 shrink-0 text-right">
                              {/* Line 1: Status badge */}
                              <Badge className={`border text-[9px] font-mono uppercase py-0 px-1.5 ${GOAL_STATUS_COLOR[goal.status] || "bg-zinc-500/15 text-zinc-400"}`}>
                                {goal.status}
                              </Badge>

                              {/* Line 2: Next reminder / Date */}
                              {reminderStr ? (
                                <span className={`text-[10px] font-mono ${reminderColor}`}>Next: {reminderStr}</span>
                              ) : (
                                <span className="text-[10px] font-mono text-muted-foreground">—</span>
                              )}

                              {/* Line 3: Action button / Last progress */}
                              {canAddProgress && goal.status === "Active" ? (
                                <Button
                                  size="sm"
                                  className="h-6 text-[9px] gap-1 px-2 font-heading uppercase tracking-wide shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedGoal(goal);
                                    setExistingEntry(null);
                                    setShowProgressModal(true);
                                  }}
                                >
                                  <TrendingUp className="h-2.5 w-2.5" /> Add progress
                                </Button>
                              ) : (
                                <span className="text-[9px] font-mono text-muted-foreground">
                                  {goal.lastProgressDate ? `Last: ${(goal.lastProgressDate || "").slice(0, 10)}` : "No progress"}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="w-full">
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                              <Target className="h-3 w-3 shrink-0 text-primary" />
                              <span className="font-medium text-foreground truncate max-w-[140px]" title={goal.name}>{goal.name}</span>
                              <Badge variant="outline" className="text-[9px] font-mono shrink-0 px-1 py-0 h-3.5 leading-none">
                                {goal.goalType}
                              </Badge>
                            </div>

                            {/* Line 3: Progress bar */}
                            <div className="flex items-center gap-2">
                              <Progress value={goal.progressPercent || 0} className="h-1 flex-1" />
                              <span className="text-[9px] font-mono text-muted-foreground shrink-0">{goal.progressPercent || 0}%</span>
                            </div>
                          </div>
                          {/* Line 2: Goal and Type */}
                        </div>
                      );
                    })}
                  </div>

                  {/* ── Desktop table (hidden below md) ── */}
                  <div className="hidden md:block overflow-auto flex-1">
                    <Table>
                      <TableHeader className="sticky top-0 bg-card z-10">
                        <TableRow className="hover:bg-transparent">
                          <TableHead>Member</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Goal</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Next Reminder</TableHead>
                          <TableHead>Last Progress</TableHead>
                          {canAddProgress && <TableHead className="text-right">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedGoals.map((goal) => (
                          <TableRow
                            key={goal.id}
                            className="group cursor-pointer hover:bg-secondary/50"
                            onClick={() => navigate(`/members/${goal.memberId}?tab=progress`)}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div>
                                  <PhotoAvatar photo={goal.member?.photo} name={goal.member?.name} />
                                </div>
                                <div className="cursor-pointer hover:text-primary transition-colors">
                                  <p className="font-medium text-sm leading-tight">
                                    <span className="text-muted-foreground">#{goal.member?.memberNumber}</span>{" "}
                                    {goal.member?.name || <span className="italic text-muted-foreground">Unknown</span>}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground font-mono uppercase">{goal.member?.gender || "—"}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{goal.member?.phone || "—"}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                                  <Target className="h-3.5 w-3.5 shrink-0 text-primary" />
                                  <span className="truncate max-w-[160px]" title={goal.name}>{goal.name}</span>
                                </span>
                                <Badge variant="outline" className="text-[10px] font-mono w-fit whitespace-nowrap">{goal.goalType}</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="w-36">
                              <div className="flex items-center gap-2">
                                <Progress value={goal.progressPercent || 0} className="flex-1 h-1.5" />
                                <span className="text-xs font-mono text-muted-foreground shrink-0 w-8 text-right">{goal.progressPercent || 0}%</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`border text-[10px] font-mono uppercase ${GOAL_STATUS_COLOR[goal.status] || "bg-zinc-500/15 text-zinc-400"}`}>
                                {goal.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {goal.nextReminderDate ? (
                                <span className={`${(goal.nextReminderDate || "").slice(0, 10) < todayStr ? "text-red-500" :
                                  (goal.nextReminderDate || "").slice(0, 10) === todayStr ? "text-amber-500" :
                                    "text-muted-foreground"
                                  }`}>
                                  {(goal.nextReminderDate || "").slice(0, 10)}
                                </span>
                              ) : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {goal.lastProgressDate ? (goal.lastProgressDate || "").slice(0, 10) : "—"}
                            </TableCell>
                            {canAddProgress && (
                              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                {goal.status === "Active" && (
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs gap-1"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedGoal(goal);
                                      setExistingEntry(null);
                                      setShowProgressModal(true);
                                    }}
                                  >
                                    <TrendingUp className="h-3 w-3" /> Add
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              {/* Pagination */}
              {!goalsLoading && pagedGoals.length > 0 && (
                <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="font-mono hidden sm:block">{goalsTotal} total</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs">Show:</span>
                      <Select value={String(goalsLimit)} onValueChange={v => { setGoalsLimit(Number(v)); setGoalsPage(1); }}>
                        <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={goalsPage === 1} onClick={() => setGoalsPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="sm:text-sm text-xs text-muted-foreground px-2">
                      Page {goalsPage} of {goalsTotalPages || 1}
                    </span>
                    <Button variant="outline" size="sm" disabled={goalsPage >= goalsTotalPages} onClick={() => setGoalsPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      {canAddProgress && (
        <AddProgressModal
          open={showProgressModal}
          onOpenChange={setShowProgressModal}
          goal={selectedGoal}
          existingEntry={existingEntry}
          selectedDate={selectedDate}
          branchId={currentBranch?.id}
          onSuccess={() => {
            fetchCalData();
            if (activeTab === "goals") fetchGoals();
          }}
        />
      )}
    </div>
  );
}
